import { useState } from "react";
import { Logo } from "@/components/Logo";
import { api, apiError } from "@/lib/api";
import { Mail, Loader2, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export default function CustomerLogin() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/auth/request-link", { email });
      setSent(true);
    } catch (err) {
      alert(apiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img
          src="https://images.pexels.com/photos/8559014/pexels-photo-8559014.jpeg?auto=compress&cs=tinysrgb&w=1200"
          alt="Colourful layered paper"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-ink/40" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <h2 className="font-heading font-black text-4xl leading-tight">Track every print, start to finish.</h2>
          <p className="text-white/80 mt-3 max-w-md">No password needed — we'll email you a secure link that keeps you signed in for 7 days.</p>
        </div>
      </div>

      <div className="flex flex-col justify-center px-6 sm:px-16 py-12">
        <div className="max-w-sm w-full mx-auto">
          <Logo />
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-ink mt-8 transition-colors">
            <ArrowLeft size={15} /> Back home
          </Link>
          {sent ? (
            <div className="mt-8 animate-fade-up" data-testid="link-sent">
              <span className="grid place-items-center w-14 h-14 rounded-full bg-orange-50 text-brand mb-5"><Mail size={26} /></span>
              <h1 className="font-heading font-black text-3xl text-ink">Check your inbox</h1>
              <p className="text-slate-600 mt-3">
                If <span className="font-semibold text-ink">{email}</span> is in our system, we've sent a secure sign-in link. It's valid for 7 days.
              </p>
              <button onClick={() => setSent(false)} className="mt-6 text-sm font-semibold text-brand hover:underline">Use a different email</button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8">
              <h1 className="font-heading font-black text-3xl text-ink">View your jobs</h1>
              <p className="text-slate-600 mt-2">Enter your email and we'll send a magic sign-in link.</p>
              <label className="block uppercase text-xs tracking-[0.18em] font-medium text-slate-500 mb-2 mt-8">Email address</label>
              <input
                type="email" required data-testid="magic-email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
              <button type="submit" disabled={sending} data-testid="send-magic-link"
                className="w-full mt-5 bg-brand hover:bg-brand-hover disabled:opacity-60 text-white font-semibold py-3.5 rounded-lg transition-colors flex items-center justify-center gap-2">
                {sending ? <><Loader2 className="animate-spin" size={18} /> Sending…</> : "Send magic link"}
              </button>
              <p className="text-xs text-slate-400 mt-4">Want to send a new job? <Link to="/submit" className="text-brand font-semibold">Submit a print request</Link></p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
